import { isShopify, useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { shopifyAdminUrl } from '@sparklayer-io/spark-dash-pages';
import { PLATFORM_PATH } from '@sparklayer-io/spark-dash-pages/constants/paths';
import { useHandlers } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { Accordion, Button, Text, TextLink } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import type { I18nPrefix } from '../../../../i18n/prefix';
import { DOCS_URLS } from '../../constants';
import { OnboardingChecklist, OnboardingChecklistItem } from './onboarding-checklist';
import { onboardingStepBadge } from './onboarding-step-badge';
import { OnboardingStepIcon } from './onboarding-step-icon';

const COMPONENT = 'features:home.components.onboarding.settings' satisfies I18nPrefix;

export function SettingsStep({
  isExpanded,
  onExpandedChange,
  loading,
  onToggle,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  loading: boolean;
  onToggle: () => void;
}) {
  const env = useEnv();
  const { navigationHandler } = useHandlers();
  const complete = env.onboarding_settings;

  // This never renders on Shopify, but cheap to keep.
  const isShopifyConnected = isShopify(env.connected_platform);
  const customersUrl = isShopifyConnected ? shopifyAdminUrl(env.store_id ?? '', 'customers') : null;
  const draftOrdersUrl = isShopifyConnected
    ? shopifyAdminUrl(env.store_id ?? '', 'draft_orders')
    : null;
  const tagOrLabelTerm = env.connected_platform === 'wix' ? 'label' : 'tag';

  const [configuredGroup, setConfiguredGroup] = useState<boolean>(false);
  const [hasCustomer, setHasCustomer] = useState<boolean>(false);
  const [placedOrder, setPlacedOrder] = useState<boolean>(false);
  const [orderVisible, setOrderVisible] = useState<boolean>(false);
  const allChecked = configuredGroup && hasCustomer && placedOrder && orderVisible;

  // Unchecking the step resets its checklist so the user re-acknowledges each item.
  useEffect(() => {
    if (!complete) {
      setConfiguredGroup(false);
      setHasCustomer(false);
      setPlacedOrder(false);
      setOrderVisible(false);
    }
  }, [complete]);

  return (
    <Accordion.Item
      title={t(`${COMPONENT}.title`)}
      subtitle={t(complete ? `${COMPONENT}.subtitle_complete` : `${COMPONENT}.subtitle_incomplete`)}
      icon={<OnboardingStepIcon complete={complete} />}
      badge={onboardingStepBadge(complete)}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    >
      <Text variant="secondary">
        <TextLink onClick={() => navigationHandler.navigate('/customers/groups')}>
          {t(`${COMPONENT}.customer_groups_link`)}
        </TextLink>{' '}
        {t(`${COMPONENT}.description`)}{' '}
        <TextLink onClick={() => navigationHandler.navigate('/customers/groups')}>
          {t(`${COMPONENT}.get_started`)}
        </TextLink>
      </Text>

      <OnboardingChecklist>
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.configured_group.label`)}
          description={t(`${COMPONENT}.checklist.configured_group.description`)}
          action={{
            onClick: () => navigationHandler.navigate('/customers/groups'),
            label: t(`${COMPONENT}.checklist.configured_group.groups_link`),
          }}
          checked={configuredGroup}
          onChange={() => setConfiguredGroup((prev) => !prev)}
        />
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.has_customer.label`)}
          description={t(`${COMPONENT}.checklist.has_customer.description`, {
            term: tagOrLabelTerm,
          })}
          action={
            customersUrl
              ? { href: customersUrl, label: t(`${COMPONENT}.checklist.has_customer.shopify_link`) }
              : undefined
          }
          learnMoreLink={DOCS_URLS.customerTags}
          checked={hasCustomer}
          onChange={() => setHasCustomer((prev) => !prev)}
        />
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.placed_order.label`)}
          description={t(`${COMPONENT}.checklist.placed_order.description`)}
          checked={placedOrder}
          onChange={() => setPlacedOrder((prev) => !prev)}
        />
        <OnboardingChecklistItem
          label={t(`${COMPONENT}.checklist.order_visible.label`)}
          description={t(`${COMPONENT}.checklist.order_visible.description`)}
          action={
            draftOrdersUrl
              ? {
                  href: draftOrdersUrl,
                  label: t(`${COMPONENT}.checklist.order_visible.shopify_link`),
                }
              : {
                  onClick: () => navigationHandler.navigate(PLATFORM_PATH),
                  label: t(`${COMPONENT}.checklist.order_visible.auto_convert_link`),
                }
          }
          checked={orderVisible}
          onChange={() => setOrderVisible((prev) => !prev)}
        />
      </OnboardingChecklist>

      <div>
        <Button
          variant={complete ? 'secondary' : 'primary'}
          disabled={!complete && !allChecked}
          loading={loading}
          onClick={onToggle}
        >
          {t(complete ? `${COMPONENT}.action_undo` : `${COMPONENT}.action`)}
        </Button>
      </div>
    </Accordion.Item>
  );
}
